import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import Head from 'next/head';
import Grid from '@mui/material/Grid';
import { useT } from '@transifex/react';
import { ContentBlockWrapper, ContentWrapper, WebBanner } from '@raisin/components-lib';
import { useAuthContext } from '@raisin/cp-layout';
import { distributionChannels, getDistributionChannel } from '@raisin/cp-utils';
import useOfferOrderLink from 'hooks/useOfferOrderLink';
import { DBSProduct, OFFER_PRODUCT_TYPES } from 'types';
import { BankDetailsType } from 'types/bank';
import { getBankPageProductTitle } from 'utils/bankPageUtils';
import { formatDefaultRate, formatTerm } from 'utils/bankPageUtils/bankOffers';
import BankOffersList from '../../BankDetails/BankOffersList/v2';
import OffersSection from '../../BankDetails/v2/OffersSection';
import bankMessages from '../../messages';
import ProductPageHeroBanner from '../Banners/Hero/v2';
import ProductPageStepper from '../Steps/v2';
import Faq from './../Faq';
import messages from './../messages';
import { RAISIN_LOGO } from './constant';
import * as S from './styles';

interface ProductPageProps {
  bank: BankDetailsType;
  offers: DBSProduct[];
  selectedOffer: DBSProduct;
  highestInterestRate: string;
  canonicalURL: string;
  bankOfferId?: string | null;
}

const ProductPage: React.FC<ProductPageProps> = ({
  bank,
  offers,
  selectedOffer,
  highestInterestRate,
  canonicalURL,
  bankOfferId,
}) => {
  const { isLoggedIn } = useAuthContext();
  const t = useT();
  const intl = useIntl();
  const { formatNumber, locale } = intl;
  const distributionChannel = getDistributionChannel(locale);

  const isTaxSectionAvailable =
    distributionChannel !== distributionChannels['en-GB'] &&
    bank?.branch_country?.source_tax_description;

  const getBankMetaDescription = (bankName: string, highestRate: string) =>
    t(messages.bankMetaDescription, {
      bank_name: bankName,
      highest_rate: `${formatNumber(Number(highestRate) || 0, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}%`,
    });

  const metaTitle = getBankPageProductTitle(bank, selectedOffer, intl, t);
  const metaDescription = getBankMetaDescription(bank.name, highestInterestRate);

  let webBannerTitle = t(messages.affiliateHeroBannerTitle, {
    interest_rate: formatDefaultRate(selectedOffer, locale, formatNumber, t),
    term: formatTerm(selectedOffer, intl, t),
    bank_name: selectedOffer?.deposit_taking_bank?.name,
  });

  if (
    selectedOffer?.product_type === OFFER_PRODUCT_TYPES.NOTICE ||
    selectedOffer?.product_type === OFFER_PRODUCT_TYPES.OVERNIGHT
  ) {
    webBannerTitle = t(messages.affiliateWebBannerOvernightOrNoticeTitle, {
      interest_rate: formatDefaultRate(selectedOffer, locale, formatNumber, t),
      bank_name: selectedOffer?.deposit_taking_bank?.name,
    });
  }
  const orderLink = useOfferOrderLink(intl.locale, selectedOffer, isLoggedIn);

  const bankOtherOffers = useMemo(
    () => offers.filter((offer) => offer.id !== selectedOffer?.id),
    [offers, selectedOffer],
  );

  return (
    <>
      <Head>
        <meta name="robots" content="noindex, follow" />
        <title>{metaTitle}</title>
        <meta name="title" content={metaTitle} />
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalURL} />
      </Head>
      <ProductPageHeroBanner
        bank={bank}
        offers={offers}
        selectedOffer={selectedOffer}
        intl={intl}
      />
      <ContentBlockWrapper hasPaddingBottom={false}>
        <ContentWrapper>
          <S.BankOffersListWrapper>
            <BankOffersList
              offers={[selectedOffer]}
              selectedOfferId={selectedOffer.id}
              expandedOffers={[selectedOffer.id]}
            />
          </S.BankOffersListWrapper>
          <WebBanner
            title={webBannerTitle}
            subTitle={t(messages.affiliateWebBannerSubTitle)}
            firstButton={{
              text: t(bankMessages.bankOffersListItemCta),
              href: orderLink,
            }}
            bankLogo={RAISIN_LOGO}
          />
          <ProductPageStepper />
          <OffersSection
            offers={bankOtherOffers}
            bank={bank}
            selectedOfferId={bankOfferId as string}
            promotedProduct={null}
            isTaxSectionAvailable={isTaxSectionAvailable}
            isProductPage={true}
          />
          <WebBanner
            title={webBannerTitle}
            subTitle={t(messages.affiliateWebBannerSubTitle)}
            firstButton={{
              text: t(bankMessages.bankOffersListItemCta),
              href: orderLink,
            }}
            bankLogo={bank?.logo_url || bank?.bank_logo_url}
          />
          <Grid container component="section" data-testid={'test'} m={0}>
            <Grid xs={12} lg={8}>
              <Faq />
            </Grid>
          </Grid>
        </ContentWrapper>
      </ContentBlockWrapper>
    </>
  );
};

export default ProductPage;
